import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check, ArrowLeft, ArrowRight, Store, User, CreditCard, MessageCircle, Send, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 40);

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();

  const [step, setStep] = useState(1); // 1=plan, 2=shop, 3=admin, 4=done
  const [plans, setPlans] = useState([]);
  const [contact, setContact] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    plan_id: params.get('piano') || 'business',
    shop_name: '',
    shop_slug: '',
    shop_city: '',
    shop_description: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_phone: '',
    accept_terms: false,
  });

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/subscription-plans`);
      setPlans(data.plans || []);
      setContact(data.contact || {});
      if (!data.plans?.find(p => p.id === form.plan_id) && data.plans?.length) {
        setForm(f => ({ ...f, plan_id: data.plans[0].id }));
      }
    } catch { toast.error('Errore piani'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const update = (patch) => {
    setForm(f => {
      const next = { ...f, ...patch };
      if (patch.shop_name !== undefined && !f.shop_slug) next.shop_slug = slugify(patch.shop_name);
      return next;
    });
  };

  const validateStep = () => {
    if (step === 1 && !form.plan_id) { toast.error('Scegli un piano'); return false; }
    if (step === 2) {
      if (!form.shop_name.trim()) { toast.error('Nome negozio richiesto'); return false; }
      return true;
    }
    if (step === 3) {
      if (!form.admin_name.trim()) { toast.error('Nome admin richiesto'); return false; }
      if (!/.+@.+\..+/.test(form.admin_email)) { toast.error('Email non valida'); return false; }
      if ((form.admin_password || '').length < 6) { toast.error('Password min 6 caratteri'); return false; }
      if (!form.accept_terms) { toast.error('Accetta i termini'); return false; }
      return true;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const prev = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/onboarding/start`, {
        ...form,
        shop_slug: slugify(form.shop_slug || form.shop_name),
      });
      setResult(data);
      setStep(4);
      // Try auto-login so user sees dashboard even while pending
      try {
        await login(form.admin_email.toLowerCase(), form.admin_password);
      } catch { /* ignore */ }
      toast.success('Account creato — procedi con il pagamento');
    } catch (e) {
      if (e.response?.status === 409) toast.error('Email già registrata');
      else toast.error(e.response?.data?.detail || 'Errore creazione account');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === form.plan_id);

  if (loading) return <div className="pt-8 text-center"><Loader2 size={32} className="animate-spin mx-auto text-[#C25934]" /></div>;

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#FDFBF7] pb-safe">
      <div className="max-w-xl mx-auto px-4 pt-4">
        {step < 4 && (
          <button onClick={() => step === 1 ? navigate('/') : prev()} data-testid="back-btn" className="p-2 rounded-full hover:bg-[#F0EBE1]">
            <ArrowLeft size={20} className="text-[#5C554F]" />
          </button>
        )}
        <div className="mt-2">
          <h1 className="text-2xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Apri il tuo negozio su PhotoVendo
          </h1>
          <div className="mt-3 flex items-center gap-1">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className={`h-1 flex-1 rounded-full ${n <= step ? 'bg-[#C25934]' : 'bg-[#E8E3D9]'}`} />
            ))}
          </div>
          <p className="text-xs text-[#877E78] mt-1">Passo {step} di 4</p>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-[#E8E3D9] p-4 space-y-4">
          {/* STEP 1: PLAN */}
          {step === 1 && (
            <div data-testid="step-plan" className="space-y-3">
              <h2 className="font-bold text-[#2A2624] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <CreditCard size={18} /> Scegli il piano
              </h2>
              {plans.map(p => (
                <button
                  key={p.id}
                  data-testid={`select-plan-${p.id}`}
                  onClick={() => update({ plan_id: p.id })}
                  className={`w-full text-left border-2 rounded-xl p-3 transition-all ${form.plan_id === p.id ? 'border-[#C25934] bg-[#C25934]/5' : 'border-[#E8E3D9] hover:border-[#C25934]/40'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-[#2A2624]">{p.name}</div>
                      {p.tagline && <div className="text-[11px] text-[#877E78]">{p.tagline}</div>}
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-[#C25934]">€{p.price_monthly}</span>
                      <span className="text-xs text-[#877E78]">/mese</span>
                    </div>
                  </div>
                  {form.plan_id === p.id && (
                    <ul className="mt-2 space-y-1 text-xs text-[#5C554F]">
                      {(p.features || []).slice(0, 4).map((f, i) => (
                        <li key={i} className="flex gap-1"><Check size={12} className="text-[#4A6B53] mt-0.5 flex-shrink-0" /> {f}</li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: SHOP DATA */}
          {step === 2 && (
            <div data-testid="step-shop" className="space-y-3">
              <h2 className="font-bold text-[#2A2624] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Store size={18} /> Dati negozio
              </h2>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Nome negozio *</label>
                <Input data-testid="shop-name" value={form.shop_name} onChange={e => update({ shop_name: e.target.value })} placeholder="Casa & Stile" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">URL personalizzato</label>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-[#877E78]">photovendo.com/negozio/</span>
                  <Input data-testid="shop-slug" value={form.shop_slug} onChange={e => update({ shop_slug: slugify(e.target.value) })} placeholder="casa-stile" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Città</label>
                <Input data-testid="shop-city" value={form.shop_city} onChange={e => update({ shop_city: e.target.value })} placeholder="Roma" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Descrizione (opzionale)</label>
                <textarea
                  data-testid="shop-description"
                  value={form.shop_description}
                  onChange={e => update({ shop_description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-[#E8E3D9] bg-white text-sm"
                  placeholder="Il tuo negozio di biancheria e casalinghi di fiducia a..."
                />
              </div>
            </div>
          )}

          {/* STEP 3: ADMIN */}
          {step === 3 && (
            <div data-testid="step-admin" className="space-y-3">
              <h2 className="font-bold text-[#2A2624] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <User size={18} /> Il tuo account admin
              </h2>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Nome completo *</label>
                <Input data-testid="admin-name" value={form.admin_name} onChange={e => update({ admin_name: e.target.value })} placeholder="Mario Rossi" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Email *</label>
                <Input data-testid="admin-email" type="email" value={form.admin_email} onChange={e => update({ admin_email: e.target.value })} placeholder="mario@miaemail.it" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Password * (min 6)</label>
                <Input data-testid="admin-password" type="password" value={form.admin_password} onChange={e => update({ admin_password: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Telefono (opzionale)</label>
                <Input data-testid="admin-phone" value={form.admin_phone} onChange={e => update({ admin_phone: e.target.value })} placeholder="+39 333 1234567" />
              </div>
              <label className="flex items-start gap-2 text-xs text-[#5C554F] cursor-pointer mt-2">
                <input
                  type="checkbox"
                  data-testid="accept-terms"
                  checked={form.accept_terms}
                  onChange={e => update({ accept_terms: e.target.checked })}
                  className="mt-0.5"
                />
                <span>Accetto i termini di servizio e la privacy policy di PhotoVendo. Confermo che acquisto il piano <b>{selectedPlan?.name}</b> a <b>€{selectedPlan?.price_monthly}/mese</b>.</span>
              </label>
            </div>
          )}

          {/* STEP 4: DONE */}
          {step === 4 && result && (
            <div data-testid="step-done" className="text-center space-y-3 py-4">
              <div className="w-16 h-16 bg-[#4A6B53]/10 rounded-full flex items-center justify-center mx-auto">
                <Check size={32} className="text-[#4A6B53]" />
              </div>
              <h2 className="text-xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                Account creato!
              </h2>
              <p className="text-sm text-[#5C554F]">
                Il tuo negozio <b>{form.shop_name}</b> è stato registrato.<br />
                URL: <code className="text-[#C25934]">photovendo.com/negozio/{result.shop_slug}</code>
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-left">
                <p className="text-xs font-bold text-amber-900 uppercase mb-1">Prossimo passo · Pagamento</p>
                <p className="text-sm text-amber-800">
                  Contatta il nostro supporto per attivare l'abbonamento <b>{result.plan?.name}</b> a <b>€{result.plan?.price_monthly}/mese</b>. Una volta ricevuto il pagamento (Stripe, PayPal o bonifico), attiveremo manualmente il tuo account.
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {contact.support_whatsapp && (
                    <a
                      data-testid="contact-whatsapp-done"
                      href={`https://wa.me/${contact.support_whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Ciao, ho appena creato il negozio "${form.shop_name}" su PhotoVendo. Voglio attivare il piano ${result.plan?.name}.`)}`}
                      target="_blank" rel="noreferrer"
                      className="h-9 rounded-full bg-[#25D366] text-white text-xs font-semibold flex items-center justify-center gap-1"
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                  {contact.support_telegram && (
                    <a
                      data-testid="contact-telegram-done"
                      href={`https://t.me/${contact.support_telegram.replace(/^@/, '')}`}
                      target="_blank" rel="noreferrer"
                      className="h-9 rounded-full bg-[#0088cc] text-white text-xs font-semibold flex items-center justify-center gap-1"
                    >
                      <Send size={12} /> Telegram
                    </a>
                  )}
                  {contact.support_email && (
                    <a
                      data-testid="contact-email-done"
                      href={`mailto:${contact.support_email}?subject=${encodeURIComponent(`Attivazione ${form.shop_name}`)}`}
                      className="h-9 rounded-full bg-[#C25934] text-white text-xs font-semibold flex items-center justify-center gap-1"
                    >
                      <Mail size={12} /> Email
                    </a>
                  )}
                </div>
                {!contact.support_whatsapp && !contact.support_telegram && !contact.support_email && (
                  <p className="text-xs text-amber-700 mt-2">
                    Canali di contatto in fase di configurazione — il SuperAdmin ti contatterà.
                  </p>
                )}
              </div>
              <div className="pt-2">
                <Button
                  data-testid="go-admin-after-onboard"
                  onClick={() => navigate('/admin')}
                  className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
                >
                  Vai alla mia dashboard <ArrowRight size={16} className="ml-2" />
                </Button>
                <p className="text-[10px] text-[#877E78] mt-1">L'account è accessibile. Attendere attivazione SuperAdmin per pubblicare prodotti.</p>
              </div>
            </div>
          )}

          {/* NAVIGATION */}
          {step < 4 && (
            <div className="pt-2 flex gap-2">
              {step > 1 && (
                <Button data-testid="prev-btn" onClick={prev} variant="outline" className="flex-1 h-11 rounded-full">
                  <ArrowLeft size={16} className="mr-1" /> Indietro
                </Button>
              )}
              {step < 3 && (
                <Button data-testid="next-btn" onClick={next} className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white">
                  Continua <ArrowRight size={16} className="ml-1" />
                </Button>
              )}
              {step === 3 && (
                <Button data-testid="submit-onboard-btn" onClick={submit} disabled={submitting} className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Crea account <ArrowRight size={16} className="ml-1" /></>}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
